import { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Headphones, 
  FileText, 
  ChevronRight 
} from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { ProductCard } from '../catalog/ProductCard.jsx';
import { HeroSlot } from '../../registry/slots/HeroSlot.jsx';
import { SHOP_CATEGORIES } from '../../lib/shop-taxonomy.js';
import { useTenant } from '../../context/TenantContext.jsx';
import { getCategories, getProducts } from '../../lib/api.js';
import { useContentBlocks, resolveContentValue, CONTENT_FALLBACKS } from '../../lib/content.js';
import { BACKGROUND_SLOTS, readBackgroundSlot } from '../../lib/design-options.js';
import { EditableText } from '../../components/editing/EditableText.jsx';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { getStorefrontTexts } from '../../lib/storefront-texts.js';
import { StorefrontHome } from './StorefrontHome.jsx';

export function HomeView({
  onNavigate,
  onViewProduct,
  onAddToCart,
}) {
  const { tenant } = useTenant();
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);

  // Content-Blocks (Issue #1): nicht blockierend laden; ohne Blöcke greifen
  // die bisher hartkodierten Fallback-Texte. Revision (Issue #3): nach
  // Inline-Speichern sofortiger Refetch -> Änderung ohne Reload sichtbar.
  const { revision } = useEditMode();
  const { map: contentMap } = useContentBlocks('home', tenant?.slug, { revision });

  // Roh-Werte (Override oder '') — leerer Wert = Standardtext im Shop UND
  // im Inline-Editor (Platzhalter „Leer lassen = Standardtext").
  const heroTitleRaw = resolveContentValue(contentMap, 'hero_title', '');
  const heroSubtitleRaw = resolveContentValue(contentMap, 'hero_subtitle', '');
  const heroCtaPrimaryRaw = resolveContentValue(contentMap, 'hero_cta_primary', '');
  const heroCtaSecondaryRaw = resolveContentValue(contentMap, 'hero_cta_secondary', '');
  const trust1TitleRaw = resolveContentValue(contentMap, 'trust_1_title', '');
  const trust1DescRaw = resolveContentValue(contentMap, 'trust_1_desc', '');
  const trust2TitleRaw = resolveContentValue(contentMap, 'trust_2_title', '');
  const trust2DescRaw = resolveContentValue(contentMap, 'trust_2_desc', '');
  const trust3TitleRaw = resolveContentValue(contentMap, 'trust_3_title', '');
  const trust3DescRaw = resolveContentValue(contentMap, 'trust_3_desc', '');
  const sectionTitleRaw = resolveContentValue(contentMap, 'section_categories_title', '');
  const sectionSubtitleRaw = resolveContentValue(contentMap, 'section_categories_subtitle', '');

  // Hero-Hintergrundbild (Design-Drawer, Issue #3 Paket B)
  const heroBackground = readBackgroundSlot(contentMap, BACKGROUND_SLOTS[0]);

  const heroTitle = heroTitleRaw;
  const heroSubtitle = heroSubtitleRaw;
  const sectionCategoriesTitle = sectionTitleRaw || CONTENT_FALLBACKS.home.section_categories_title;
  const sectionCategoriesSubtitle = sectionSubtitleRaw || CONTENT_FALLBACKS.home.section_categories_subtitle;

  const tenantName = tenant?.name || 'ASL Partner-Shop';

  useEffect(() => {
    let isMounted = true;
    async function loadHomeData() {
      try {
        const tenantId = tenant?.id;
        const [catData, prodData] = await Promise.allSettled([
          getCategories(tenantId),
          getProducts({ tenantId, limit: 6, sort: 'recommended' }),
        ]);

        if (isMounted) {
          if (catData.status === 'fulfilled' && catData.value?.categories) {
            setCategories(catData.value.categories);
            const total = catData.value.categories.reduce((sum, c) => sum + (c.productCount || c.product_count || 0), 0);
            setTotalProducts(total);
          }
          if (prodData.status === 'fulfilled' && (prodData.value?.products || prodData.value?.items)) {
            setFeaturedProducts(prodData.value.products || prodData.value.items || []);
          }
        }
      } catch (err) {
        console.warn('Error loading home view data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadHomeData();
    return () => { isMounted = false; };
  }, [tenant?.id]);

  const isHighlightGrid = (tenant?.settings?.layout_hero || tenant?.layout_hero) === 'highlight_grid';
  const defaultHeroCtaPrimary = isHighlightGrid ? 'Gesamten Katalog öffnen' : CONTENT_FALLBACKS.home.hero_cta_primary;
  const defaultHeroCtaSecondary = isHighlightGrid ? 'Projektangebot anfragen' : CONTENT_FALLBACKS.home.hero_cta_secondary;

  const trustPoints = [
    {
      id: 'trust-1',
      title: (
        <EditableText page="home" blockKey="trust_1_title" value={trust1TitleRaw} data-testid="edit-trust-1-title">
          {trust1TitleRaw || CONTENT_FALLBACKS.home.trust_1_title}
        </EditableText>
      ),
      description: (
        <EditableText page="home" blockKey="trust_1_desc" value={trust1DescRaw} data-testid="edit-trust-1-desc">
          {trust1DescRaw || `Fachliche Beratung durch ${tenantName}.`}
        </EditableText>
      ),
      icon: Headphones,
    },
    {
      id: 'trust-2',
      title: (
        <EditableText page="home" blockKey="trust_2_title" value={trust2TitleRaw} data-testid="edit-trust-2-title">
          {trust2TitleRaw || CONTENT_FALLBACKS.home.trust_2_title}
        </EditableText>
      ),
      description: (
        <EditableText page="home" blockKey="trust_2_desc" value={trust2DescRaw} data-testid="edit-trust-2-desc">
          {trust2DescRaw || `${totalProducts > 0 ? `${totalProducts.toLocaleString('de-DE')} ` : ''}Fachartikel für Sicherheitstechnik.`}
        </EditableText>
      ),
      icon: ShieldCheck,
    },
    {
      id: 'trust-3',
      title: (
        <EditableText page="home" blockKey="trust_3_title" value={trust3TitleRaw} data-testid="edit-trust-3-title">
          {trust3TitleRaw || CONTENT_FALLBACKS.home.trust_3_title}
        </EditableText>
      ),
      description: (
        <EditableText page="home" blockKey="trust_3_desc" value={trust3DescRaw} data-testid="edit-trust-3-desc">
          {trust3DescRaw || CONTENT_FALLBACKS.home.trust_3_desc}
        </EditableText>
      ),
      icon: FileText,
    },
  ];

  // a-045: Mandant mit eigenen Startseiten-Texten -> eigene Komposition.
  // Ohne Texte bleibt die Master-Startseite unverändert (Fallback).
  const storefrontTexts = getStorefrontTexts(tenant);
  if (storefrontTexts) {
    return (
      <StorefrontHome
        tenant={tenant}
        texts={storefrontTexts}
        categories={categories}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 space-y-16">
      
      {/* DYNAMIC HERO SLOT */}
      <HeroSlot
        variant={tenant?.settings?.layout_hero}
        tenant={tenant}
        catalogCount={totalProducts}
        onSearch={(term) => onNavigate(`/catalog?search=${encodeURIComponent(term)}`)}
        onSelectCategory={(slug) => onNavigate(`/catalog/${slug}`)}
        onNavigate={onNavigate}
        featuredProduct={featuredProducts[0] || null}
        categories={categories}
        onAddToCart={onAddToCart}
        onViewProduct={onViewProduct}
        backdropUrl={heroBackground.imageUrl}
        backdropStrength={heroBackground.strength}
        heroTitle={
          <EditableText page="home" blockKey="hero_title" value={heroTitleRaw} data-testid="edit-hero-title">
            {heroTitleRaw || <>Sicherheitstechnik <span className="text-[var(--primary)]">direkt finden</span> &amp; disponieren.</>}
          </EditableText>
        }
        heroSubtitle={
          <EditableText page="home" blockKey="hero_subtitle" value={heroSubtitleRaw} data-testid="edit-hero-subtitle">
            {heroSubtitleRaw || `Gezielte SKU-, Hersteller- und Artikelsuche im ${tenantName} Großhandelsbestand für Errichter und Fachplaner.`}
          </EditableText>
        }
        heroCtaPrimary={
          <EditableText page="home" blockKey="hero_cta_primary" value={heroCtaPrimaryRaw} data-testid="edit-hero-cta-primary">
            {heroCtaPrimaryRaw || defaultHeroCtaPrimary}
          </EditableText>
        }
        heroCtaSecondary={
          <EditableText page="home" blockKey="hero_cta_secondary" value={heroCtaSecondaryRaw} data-testid="edit-hero-cta-secondary">
            {heroCtaSecondaryRaw || defaultHeroCtaSecondary}
          </EditableText>
        }
      />

      {/* TRUST & VALUE PROPOSITION STRIP */}
      <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {trustPoints.map((point) => {
          const Icon = point.icon;
          return (
            <div 
              key={point.id}
              className="rounded-2xl border border-[var(--border)] bg-white p-6 transition-all hover:border-[var(--primary)]/50 hover:shadow-sm space-y-3"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--primary)]/10 text-[var(--primary)]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-bold text-[var(--foreground)]">{point.title}</h3>
              <p className="text-sm text-[var(--secondary)] leading-relaxed">{point.description}</p>
            </div>
          );
        })}
      </section>

      {/* CATEGORY GRID */}
      <section className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--primary)]">Sortiment</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
              <EditableText page="home" blockKey="section_categories_title" value={sectionTitleRaw} data-testid="edit-section-title">
                {sectionCategoriesTitle}
              </EditableText>
            </h2>
            <p className="mt-2 text-sm text-[var(--secondary)]">
              <EditableText page="home" blockKey="section_categories_subtitle" value={sectionSubtitleRaw} data-testid="edit-section-subtitle">
                {sectionCategoriesSubtitle}
              </EditableText>
            </p>
          </div>
          <Button variant="outline" onClick={() => onNavigate('/catalog')}>
            Alle Fachbereiche anzeigen
          </Button>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SHOP_CATEGORIES.map((category, catIdx) => {
            const catData = categories.find((c) => c.slug === category.slug);
            const count = catData?.productCount || catData?.product_count || 0;
            if (!count) return null; // leere Fachbereiche (z. B. Weitere Produkte) nicht als leere Kachel zeigen

            const catDelay = `${Math.min(catIdx * 20, 200)}ms`;

            return (
              <button
                key={category.slug}
                onClick={() => onNavigate(`/catalog/${category.slug}`)}
                style={{
                  animationDelay: catDelay,
                  '--stagger-delay': catDelay,
                }}
                className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-[var(--border)] bg-white p-6 text-left transition-all hover:border-[var(--primary)] hover:shadow-md cursor-pointer anim-card-stagger"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="rounded-md bg-[var(--primary)]/10 px-2.5 py-1 text-xs font-bold text-[var(--primary)]">
                      {category.shortLabel || category.label}
                    </span>
                    <span className="text-xs font-semibold text-[var(--secondary)]">
                      {count > 0 ? `${count} Artikel` : 'Sortiment'}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-display text-xl font-bold text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors">
                      {category.label}
                    </h3>
                    <p className="mt-2 text-sm text-[var(--secondary)] line-clamp-2">
                      {category.description}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {category.subcategories.slice(0, 3).map((sub) => (
                      <span 
                        key={sub.slug}
                        className="rounded border border-[var(--border)] bg-[var(--surface-container-low)] px-2 py-0.5 text-xs font-medium text-[var(--secondary)]"
                      >
                        {sub.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-[var(--border)] pt-4 text-xs font-bold text-[var(--foreground)] group-hover:text-[var(--primary)]">
                  <span>Fachbereich öffnen</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featuredProducts.length > 0 && (
        <section className="space-y-8 border-t border-[var(--border)] pt-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--primary)]">Empfehlungen</p>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
                Ausgewählte Produkte
              </h2>
            </div>
            <Button variant="outline" onClick={() => onNavigate('/catalog')}>
              Katalog durchsuchen
            </Button>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredProducts.map((product, idx) => (
              <ProductCard
                key={product.id || product.sku}
                product={product}
                index={idx}
                onViewProduct={(slugOrId) => {
                  if (onViewProduct) onViewProduct(slugOrId);
                  else onNavigate(`/products/${slugOrId}`);
                }}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
